'use client';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { Stream, Streamable } from '@/vibes/mom/lib/streamable';
import { AccountDetails, TopLevelMenuItem } from '~/components/header';
import { Link } from '~/components/link';
import { env } from '~/lib/atom/env';

import { Button } from '../button';

interface Props {
  links: Streamable<TopLevelMenuItem[]> | Promise<TopLevelMenuItem[]>;
  logo?: Streamable<string | { src: string; alt: string } | null>;
  logoLabel?: string;
  logoHref?: string;
  accountDetails?: Streamable<AccountDetails | null>;
}

const baseMenuItemClasses =
  'group/button flex w-full items-center justify-between px-4 py-2 font-semibold font-heading-condensed tracking-wider uppercase bg-mom-brandColorDarker border-y-[1px] border-mom-brandColor';

export const MobileNav = ({ links, accountDetails }: Props) => {
  const [open, setOpen] = useState(false);
  const t = useTranslations('Components.Header.Navigation');

  const handleClickedLink = () => {
    setOpen(false);
  };

  return (
    <SheetPrimitive.Root onOpenChange={setOpen} open={open}>
      <SheetPrimitive.Trigger asChild>
        <Button
          aria-controls="nav-menu"
          aria-label={t('toggle')}
          className="group !h-[50px] !w-[60px] bg-transparent !p-0 text-white hover:bg-transparent hover:text-slate-200 lg:hidden"
          loading={false}
          shape="rounded"
          variant="ghost"
        >
          <Menu className="h-7 w-14" />
        </Button>
      </SheetPrimitive.Trigger>
      <SheetPrimitive.Portal>
        <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <SheetPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 left-0 z-50 h-full w-full overflow-y-auto border-r border-mom-brandColor bg-mom-brandColorDarker text-white shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm"
        >
          <SheetPrimitive.Title asChild>
            <h2 className="sr-only">{t('navigationMenu')}</h2>
          </SheetPrimitive.Title>
          <div className="flex bg-mom-black p-4">
            <div className="flex flex-col justify-end gap-4">
              <Stream
                fallback={
                  <>
                    <h2 className="font-heading text-xl font-normal text-white">
                      {t('hey')}{' '}
                      <strong className="h-4 w-12 animate-pulse rounded-md bg-contrast-200" />!
                    </h2>
                    <div className="flex gap-2">
                      <Link
                        className="border-solid-white hover:border-solid-mom-black flex w-full rounded-lg border-1 border-contrast-500 p-3 font-heading-condensed text-sm font-semibold uppercase leading-4 tracking-wider text-white transition hover:border-contrast-100 hover:bg-white hover:text-mom-black"
                        href="/login"
                        role="button"
                        tabIndex={0}
                      >
                        Log in
                      </Link>
                      <Link
                        className="border-solid-white hover:border-solid-mom-black flex w-full rounded-lg border-1 border-contrast-500 p-3 font-heading-condensed text-sm font-semibold uppercase leading-4 tracking-wider text-white transition hover:border-contrast-100 hover:bg-white hover:text-mom-black"
                        href={
                          env.NEXT_PUBLIC_SITE_TYPE === 'B2B'
                            ? '/trade-registration/'
                            : '/register/'
                        }
                        role="button"
                        tabIndex={0}
                      >
                        Register
                      </Link>
                    </div>
                  </>
                }
                value={accountDetails}
              >
                {(user) => {
                  return user != null ? (
                    <>
                      <div className="flex flex-col gap-2">
                        <h2 className="font-heading text-xl font-normal text-white">
                          {t('hey')} <strong className="capitalize">{user.firstName}</strong>!
                        </h2>
                      </div>
                      <div className="flex gap-2">
                        <Link
                          className="border-solid-white hover:border-solid-mom-black flex w-full rounded-lg border-1 border-contrast-500 p-3 font-heading-condensed text-sm font-semibold uppercase leading-4 tracking-wider text-white transition hover:border-contrast-100 hover:bg-white hover:text-mom-black"
                          href="/account/orders"
                          onClick={handleClickedLink}
                          role="button"
                          tabIndex={0}
                        >
                          {t('account')}
                        </Link>
                        <Link
                          className="border-solid-white hover:border-solid-mom-black flex w-full rounded-lg border-1 border-contrast-500 p-3 font-heading-condensed text-sm font-semibold uppercase leading-4 tracking-wider text-white transition hover:border-contrast-100 hover:bg-white hover:text-mom-black"
                          href="/account/orders"
                          onClick={handleClickedLink}
                          role="button"
                          tabIndex={0}
                        >
                          {t('orders')}
                        </Link>
                        <Link
                          className="border-solid-white hover:border-solid-mom-black flex w-full rounded-lg border-1 border-contrast-500 p-3 font-heading-condensed text-sm font-semibold uppercase leading-4 tracking-wider text-white transition hover:border-contrast-100 hover:bg-white hover:text-mom-black"
                          href="/account/wishlists"
                          onClick={handleClickedLink}
                          role="button"
                          tabIndex={0}
                        >
                          {t('wishlists')}
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="font-heading text-xl font-normal text-white">Hello !</h2>
                      <div className="flex gap-2">
                        <Link
                          className="border-solid-white hover:border-solid-mom-black flex w-full rounded-lg border-1 border-contrast-500 p-3 font-heading-condensed text-sm font-semibold uppercase leading-4 tracking-wider text-white transition hover:border-contrast-100 hover:bg-white hover:text-mom-black"
                          href="/login"
                          onClick={handleClickedLink}
                          role="button"
                          tabIndex={0}
                        >
                          {t('login')}
                        </Link>
                        <Link
                          className="border-solid-white hover:border-solid-mom-black flex w-full rounded-lg border-1 border-contrast-500 p-3 font-heading-condensed text-sm font-semibold uppercase leading-4 tracking-wider text-white transition hover:border-contrast-100 hover:bg-white hover:text-mom-black"
                          href={
                            env.NEXT_PUBLIC_SITE_TYPE === 'B2B'
                              ? '/trade-registration/'
                              : '/register/'
                          }
                          onClick={handleClickedLink}
                          role="button"
                          tabIndex={0}
                        >
                          {t('register')}
                        </Link>
                      </div>
                    </>
                  );
                }}
              </Stream>
            </div>
            <SheetPrimitive.DialogClose className="-mt-4 ml-auto flex self-start">
              <div className="p-4">
                <X className="h-6 w-6" />
              </div>
            </SheetPrimitive.DialogClose>
          </div>
          <NavigationMenuPrimitive.Root delayDuration={0} orientation="vertical">
            <NavigationMenuPrimitive.List className="flex flex-col">
              <Stream
                fallback={
                  <ul className="flex animate-pulse flex-col gap-4 p-5 @4xl:gap-2 @4xl:p-5">
                    <li>
                      <span className="block h-4 w-10 rounded-md bg-contrast-100" />
                    </li>
                    <li>
                      <span className="block h-4 w-14 rounded-md bg-contrast-100" />
                    </li>
                    <li>
                      <span className="block h-4 w-24 rounded-md bg-contrast-100" />
                    </li>
                    <li>
                      <span className="block h-4 w-16 rounded-md bg-contrast-100" />
                    </li>
                  </ul>
                }
                value={links}
              >
                {(builderMobileNav) =>
                  builderMobileNav.map((topLevelItem: TopLevelMenuItem) =>
                    topLevelItem.columns && topLevelItem.columns.length > 0 ? (
                      <NavigationMenuPrimitive.Item
                        className="border-mom-brandColor"
                        key={topLevelItem.url}
                      >
                        <NavigationMenuPrimitive.Trigger
                          className={`${baseMenuItemClasses} bg-[color:var(--background-color)] text-[color:var(--text-color)] shadow-[0_2px_0_mom-teal] hover:bg-mom-brandColor hover:text-white focus:bg-mom-brandColor focus:text-white`}
                          style={
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                            {
                              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                              ['--background-color' as string]: topLevelItem.backgroundColour,
                              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                              ['--text-color' as string]: topLevelItem.textColour,
                              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                              ['--hover-background-color' as string]:
                                topLevelItem.hoverBackgroundColour,
                              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                              ['--hover-text-color' as string]: topLevelItem.hoverTextColour,
                            } as React.CSSProperties
                          }
                        >
                          <span className="font-semibold">{topLevelItem.name}</span>
                          <ChevronDown
                            aria-hidden="true"
                            className="cursor-pointer text-contrast-400 transition duration-200 group-data-[state=open]/button:-rotate-180"
                          />
                        </NavigationMenuPrimitive.Trigger>
                        <NavigationMenuPrimitive.Content className="flex flex-col border-mom-brandColor duration-200 animate-in slide-in-from-top-2">
                          {/* Second Level */}
                          <NavigationMenuPrimitive.Root>
                            <NavigationMenuPrimitive.List className="flex flex-col">
                              {topLevelItem.columns.map((secondLevelItem, index) => (
                                <NavigationMenuPrimitive.Item key={index}>
                                  {/* eslint-disable no-nested-ternary */}
                                  {secondLevelItem.children &&
                                  secondLevelItem.children.length > 0 ? (
                                    <>
                                      <NavigationMenuPrimitive.Trigger
                                        className={`${baseMenuItemClasses} border-b border-mom-brandColor bg-mom-brandColorDarker pl-5 pr-5 hover:bg-mom-brandColor hover:text-white focus:bg-mom-brandColor focus:text-white focus-visible:outline-none`}
                                      >
                                        <span className="font-semibold">
                                          {secondLevelItem.name}
                                        </span>
                                        <ChevronDown
                                          aria-hidden="true"
                                          className="cursor-pointer transition duration-200 group-data-[state=open]/button:-rotate-180"
                                        />
                                      </NavigationMenuPrimitive.Trigger>
                                      <NavigationMenuPrimitive.Content className="flex flex-col duration-200 animate-in slide-in-from-top-2">
                                        {secondLevelItem.children.map(
                                          (thirdLevelItem, childIndex) => (
                                            <div key={`third-level-${childIndex}`}>
                                              <NavigationMenuPrimitive.Link
                                                asChild
                                                className={`${baseMenuItemClasses} border-b border-mom-brandColor bg-mom-brandColorDarker pl-7 pr-7 text-white hover:bg-mom-brandColorDarker hover:text-white focus:bg-mom-brandColorDarker focus:text-white`}
                                              >
                                                <Link
                                                  href={thirdLevelItem.url}
                                                  onClick={handleClickedLink}
                                                  prefetch="viewport"
                                                >
                                                  {thirdLevelItem.name}
                                                </Link>
                                              </NavigationMenuPrimitive.Link>
                                              {thirdLevelItem.children &&
                                                thirdLevelItem.children.length > 0 && (
                                                  <>
                                                    {thirdLevelItem.children.map(
                                                      (nestedChild, nestedChildIndex) => (
                                                        <NavigationMenuPrimitive.Link
                                                          asChild
                                                          className={`${baseMenuItemClasses} border-b border-mom-brandColor bg-mom-brandColorDarker pl-7 pr-7 text-[color:var(--text-color)] hover:bg-mom-brandColorDarker hover:text-white focus:bg-mom-brandColorDarker focus:text-white`}
                                                          key={nestedChildIndex}
                                                        >
                                                          <Link
                                                            href={nestedChild.url}
                                                            onClick={handleClickedLink}
                                                            prefetch="viewport"
                                                          >
                                                            {nestedChild.name}
                                                          </Link>
                                                        </NavigationMenuPrimitive.Link>
                                                      ),
                                                    )}
                                                  </>
                                                )}
                                            </div>
                                          ),
                                        )}
                                      </NavigationMenuPrimitive.Content>
                                    </>
                                  ) : secondLevelItem.imageItem ? (
                                    <NavigationMenuPrimitive.Link
                                      asChild
                                      className="flex min-h-[116px] gap-4 bg-mom-brandColorDarker px-5 py-2"
                                    >
                                      <Link
                                        data-promo={secondLevelItem.imageItem.promo}
                                        data-promo-creative={
                                          secondLevelItem.imageItem.promoCreative
                                        }
                                        href={secondLevelItem.imageItem.destinationUrl}
                                        itemProp="url"
                                        onClick={handleClickedLink}
                                        prefetch="viewport"
                                        title={secondLevelItem.imageItem.headingText}
                                      >
                                        <div className="min-w-24 self-center">
                                          {/* eslint-disable @next/next/no-img-element */}
                                          <img
                                            alt={secondLevelItem.imageItem.imageAltText}
                                            height={100}
                                            loading="lazy"
                                            src={secondLevelItem.imageItem.image}
                                            width={100}
                                          />
                                        </div>
                                        <div className="self-center">
                                          <p className="font-bold">
                                            {secondLevelItem.imageItem.headingText}
                                          </p>
                                          <p className="break-words text-sm">
                                            {secondLevelItem.imageItem.tagline}
                                          </p>
                                        </div>
                                      </Link>
                                    </NavigationMenuPrimitive.Link>
                                  ) : null}
                                </NavigationMenuPrimitive.Item>
                              ))}
                            </NavigationMenuPrimitive.List>
                          </NavigationMenuPrimitive.Root>
                        </NavigationMenuPrimitive.Content>
                      </NavigationMenuPrimitive.Item>
                    ) : (
                      <NavigationMenuPrimitive.Item key={topLevelItem.url}>
                        <NavigationMenuPrimitive.Link
                          asChild
                          className="block w-full p-3 ps-0 font-semibold"
                        >
                          <Link href={topLevelItem.url} onClick={handleClickedLink}>
                            {topLevelItem.name}
                          </Link>
                        </NavigationMenuPrimitive.Link>
                      </NavigationMenuPrimitive.Item>
                    ),
                  )
                }
              </Stream>
            </NavigationMenuPrimitive.List>
          </NavigationMenuPrimitive.Root>
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    </SheetPrimitive.Root>
  );
};
